// The platform's Calendar settings, using only the same scoped app endpoints
// as the public runtime helper. Nothing is saved to device storage.
import { getJSON, postJSON } from './api.js';

export const CALENDAR_STATE = Object.freeze({
  relevant: false, enabled: false, connected: false,
  loading: false, busy: false, error: null, notice: null,
});

export function calendarError(error) {
  switch (error?.code) {
    case 'google_consent_required':
    case 'google_scope_required': return 'Connect Google Calendar again to approve access.';
    case 'google_access_denied': return 'Google Calendar access was not approved. You can try again.';
    case 'google_busy': return 'Google Calendar is busy. Please try again shortly.';
    case 'unauthorized': return 'This device no longer has access. Reconnect the app, then try again.';
    case 'network':
    case 'offline':
    case 'timeout': return 'Couldn’t reach the server. Check your connection and try again.';
    default: return 'Couldn’t update Google Calendar access. Please try again.';
  }
}

export function createCalendarConnection({ request = { getJSON, postJSON }, onChange = () => {}, navigate = (url) => globalThis.location.assign(url), signal = null } = {}) {
  let state = { ...CALENDAR_STATE };
  let generation = 0;
  let stopped = false;
  const update = (patch) => { state = { ...state, ...patch }; onChange(state); };
  return {
    state: () => state,
    use() { if (!stopped) { update({ relevant: true }); } },
    // Status errors never turn a connected grant into a disconnected one.
    // Older probes cannot overwrite a disconnect or a later refresh.
    async refresh({ force = false } = {}) {
      if (stopped || state.busy || (state.loading && !force)) return;
      const current = ++generation;
      const startedAt = signal?.revision();
      update({ loading: true, error: null });
      try {
        const out = await request.getJSON('/v1/app/google/calendar', { timeout: 10_000 });
        if (stopped || current !== generation) return;
        if (typeof out?.enabled !== 'boolean' || typeof out?.connected !== 'boolean') throw new Error('Bad status');
        // Overtaken by a disconnect, a "connected" answer is out of date, but
        // a "disconnected" one is not: the app's own read can land first and
        // move the revision, and the panel must not keep saying Connected.
        if (signal && startedAt !== signal.revision()) {
          update(out.connected === false ? { loading: false, connected: false } : { loading: false });
          return;
        }
        signal?.observe(out, startedAt);
        update({ loading: false, enabled: out?.enabled === true, connected: out?.connected === true,
          relevant: state.relevant || out?.connected === true, notice: out?.connected === true ? null : state.notice });
      } catch (error) {
        if (!stopped && current === generation) update({ loading: false, error: calendarError(error) });
      }
    },
    async connect() {
      if (stopped || state.busy || state.loading) return false;
      ++generation;
      update({ relevant: true, busy: true, error: null, notice: null });
      try {
        const out = await request.postJSON('/v1/app/google/calendar/connect', {});
        if (stopped) return false;
        if (typeof out?.url !== 'string' || !out.url) throw new Error('Bad consent URL');
        navigate(out.url);
        update({ busy: false });
        return true;
      } catch (error) {
        if (!stopped) update({ busy: false, error: calendarError(error) });
        return false;
      }
    },
    async disconnect() {
      if (stopped || state.busy || !state.connected) return false;
      ++generation;
      update({ relevant: true, busy: true, loading: false, error: null, notice: null });
      try {
        const out = await request.postJSON('/v1/app/google/calendar/disconnect', {});
        if (stopped) return false;
        if (out?.connected !== false) throw new Error('Bad disconnect response');
        signal?.disconnected(out);
        update({ busy: false, connected: false, notice: out?.revocation_pending === true
          ? 'Disconnected from this app. Removing access at Google is still pending. Mytheseus will try again automatically.'
          : 'Google Calendar is disconnected from this app.' });
        return true;
      } catch (error) {
        if (!stopped) update({ busy: false, error: calendarError(error) });
        return false;
      }
    },
    stop() { stopped = true; ++generation; signal?.dispose(); update({ busy: false, loading: false, connected: false, relevant: false }); },
  };
}
